import { useQuery } from '@tanstack/react-query'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { fetchLumpsumRebalancePlanRecommendation } from '../../api/repository'
import { LUMPSUM_PLANS_API_RETRY_COUNT, isLumpsumQueryFetchFailed, isLumpsumQueryLoading } from '../../../api/queryOptions'
import type {
  LumpsumRebalanceBuyPlanRecommendationDetails,
  LumpsumRebalancePlanRecommendationApiResponse
} from '../../api/rebalanceRecommendation.types'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { RebalanceBuyPlanRecommendationSheet } from './RebalanceBuyPlanRecommendationSheet'

const REBALANCE_BUY_PLAN_RECOMMENDATION_QUERY_KEY = 'lumpsum-plans-v4-rebalance-buy-recommendation'

export function RebalanceBuyPlanRecommendationContainer(input: {
  onDismiss: () => void
  isin: string
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}) {
  const { onDismiss, isin, onMissedGainsInfoClick, transactionCta } = input
  const { planDetails, isLoading, isFetchFailed, onRetry } = useRebalanceBuyPlanRecommendation(isin, true)

  return (
    <RebalanceBuyPlanRecommendationSheet
      onDismiss={onDismiss}
      isin={isin}
      planDetails={planDetails}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      onMissedGainsInfoClick={onMissedGainsInfoClick}
      transactionCta={transactionCta}
    />
  )
}

function useRebalanceBuyPlanRecommendation(isin: string, enabled: boolean): {
  planDetails: LumpsumRebalanceBuyPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
} {
  const query = useQuery({
    queryKey: [REBALANCE_BUY_PLAN_RECOMMENDATION_QUERY_KEY, isin],
    queryFn: () =>
      fetchLumpsumRebalancePlanRecommendation(isin, LumpsumRecommendationActionType.BUY),
    enabled: enabled && isin.length > 0,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })
  const response = query.data ?? null

  return {
    planDetails: isBuyPlanRecommendationDetails(response) ? response : null,
    isLoading: isLumpsumQueryLoading(query),
    isFetchFailed: isLumpsumQueryFetchFailed(query),
    onRetry: () => {
      void query.refetch()
    }
  }
}

function isBuyPlanRecommendationDetails(
  response: LumpsumRebalancePlanRecommendationApiResponse | null
): response is LumpsumRebalanceBuyPlanRecommendationDetails {
  return response != null && 'fundsToBeSold' in response
}
